/**
 * @ngdoc     directive
 * @name      csm.ui.validators.directive:csmValidators
 * @restrict  'A'
 * @element   ANY
 * @param     {object}  csmValidators  Validators control object.
 * @description
 * Validators control directive. This is the parent of validator directives
 * created with `CSMValidatorService.makeDirective()` and found on child elements.
 * Validator directives require this directive on a parent element and interact
 * with this directive's controller methods:
 *
 *   * `addValidator`    - Adds the validator to the control object.
 *   * `removeValidator` - Removes the validator from the control object.
 *
 * The control object is empty object supplied in the `csmValidators` attribute.
 * Methods are added to this object giving the host controller validation control:
 *
 *   * `reset`    - Resets the validators, clearing all validation messages.
 *   * `validate` - Executes the validators and displays validation messages.
 **/

/* @ngInject */
export function csmValidators($injector) {
  /*************************************************************************
   * $ctrl.addValidator */
  /**
   * Called by validator directives to be added to the list.
   *
   * @param {object} validator Validator to add.
   *************************************************************************/
  /* @ngInject */ function addValidator($ctrl) {
    return function (validator) {
      if (
        !$ctrl.validators.some(function (v) {
          return v.element[0] == validator.element[0];
        })
      )
        $ctrl.validators.push(validator);
    };
  }

  /*************************************************************************
   * $ctrl.removeValidator */
  /**
   * Called by validator directives to be removed from the list.
   *
   * @param {object} validator Validator to remove.
   *************************************************************************/
  /* @ngInject */ function removeValidator($ctrl) {
    return function (validator) {
      $ctrl.validators = $ctrl.validators.filter(function (v) {
        if (v != validator) return true;
        validator.reset();
        return false;
      });
    };
  }

  /*************************************************************************
   * csmValidators.reset */
  /**
   * Calls the `reset` method of each validator.
   *************************************************************************/
  /* @ngInject */ function reset($ctrl) {
    return function () {
      $ctrl.validators.forEach(function (validator) {
        validator.reset();
      });
    };
  }

  /***********************************************************************
   * csmValidators.validate */
  /**
   * Calls the `validate` method of each validator.
   *
   * @param  {boolean} focusInvalid `true`=move focus to first element where
   *                   validation failed. Default=`true`.
   * @return {boolean} `true`=validation successful.
   ***********************************************************************/
  /* @ngInject */ function validate($ctrl, $timeout) {
    return function (focusInvalid) {
      var valid = true;

      $ctrl.validators.forEach(function (validator) {
        if (!validator.validate() && valid) {
          valid = false;

          if (angular.isUndefined(focusInvalid) ? true : focusInvalid) {
            var e = validator.element;
            e[0].focus();
            $timeout(function () {
              e.triggerHandler('mouseenter');
            });
          }
        }
      });

      return valid;
    };
  }

  return {
    restrict: 'A',
    controller: function ($scope, $attrs) {
      var $svcs = { $ctrl: this, $scope: $scope, $attrs: $attrs };
      var $ctrl = Object.assign(this, {
        validators: [],
        addValidator: $injector.invoke(addValidator, null, $svcs),
        removeValidator: $injector.invoke(removeValidator, null, $svcs)
      });

      $scope.$watch(
        function () {
          return $scope.$eval($attrs['csmValidators']);
        },
        function (csmValidators) {
          if (csmValidators) {
            if (!csmValidators.hasOwnProperty('validators'))
              Object.assign(csmValidators, {
                reset: $injector.invoke(reset, null, $svcs),
                validate: $injector.invoke(validate, null, $svcs),
                validators: []
              });

            $ctrl.validators
              .filter(function (v) {
                return csmValidators.validators.indexOf(v) < 0;
              })
              .forEach(function (v) {
                csmValidators.validators.push(v);
              });

            $ctrl.validators = csmValidators.validators;
          }
        }
      );
    }
  };
}
